import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";
import {
  Bell, Shield, Map, Mail, MessageSquare, IdCard, Plug,
  Save, Loader2, CheckCircle2, Circle, Eye, EyeOff, RotateCcw, Trash2,
} from "lucide-react";

const ICONS = { bell: Bell, shield: Shield, map: Map, mail: Mail, "message-square": MessageSquare, "id-card": IdCard };

function StatusPill({ configured }) {
  return configured ? (
    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
      <CheckCircle2 size={12} /> Active
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-slate-50 text-slate-500 border border-slate-200">
      <Circle size={12} /> Not configured
    </span>
  );
}

function FieldInput({ field, value, onChange }) {
  const [show, setShow] = useState(false);
  if (field.type === "boolean") {
    return (
      <label className="flex items-center gap-3 cursor-pointer">
        <input
          type="checkbox"
          data-testid={`integ-field-${field.name}`}
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
          className="w-4 h-4 accent-primary"
        />
        <span className="text-sm text-slate-700">{field.label}</span>
      </label>
    );
  }
  if (field.type === "select") {
    return (
      <div>
        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">{field.label}</label>
        <select
          data-testid={`integ-field-${field.name}`}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="mt-1 w-full h-11 px-3 rounded-lg border border-slate-200 bg-white focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
        >
          <option value="">Select…</option>
          {(field.options || []).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      </div>
    );
  }
  const isPassword = field.type === "password";
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">{field.label}</label>
      <div className="relative mt-1">
        <input
          data-testid={`integ-field-${field.name}`}
          type={isPassword && !show ? "password" : "text"}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={isPassword ? "Leave blank to keep current" : ""}
          className="w-full h-11 px-3 pr-10 rounded-lg border border-slate-200 bg-white focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
            tabIndex={-1}
          >
            {show ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        )}
      </div>
    </div>
  );
}

function IntegrationCard({ integ, onSaved }) {
  const Icon = ICONS[integ.icon] || Plug;
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(integ.value || {});
  const [saving, setSaving] = useState(false);

  useEffect(() => setValue(integ.value || {}), [integ]);

  async function save() {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/integrations/${integ.key}`, { value });
      toast.success(`${integ.name} saved`);
      onSaved(integ.key, data.value);
      setOpen(false);
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSaving(false);
    }
  }

  async function resetAll() {
    if (!window.confirm(`Clear all stored settings for ${integ.name}?`)) return;
    try {
      await api.delete(`/admin/integrations/${integ.key}`);
      toast.success("Cleared");
      onSaved(integ.key, {});
      setValue({});
    } catch (e) {
      toast.error(fmtErr(e));
    }
  }

  return (
    <div
      data-testid={`integ-card-${integ.key}`}
      className="bg-white rounded-2xl border border-slate-200 overflow-hidden transition hover:border-primary/40 hover:shadow-md"
    >
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full px-5 py-4 flex items-center gap-4 text-left"
        data-testid={`integ-toggle-${integ.key}`}
      >
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${integ.configured ? "bg-primary text-white" : "bg-slate-100 text-slate-500"}`}>
          <Icon size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-slate-900">{integ.name}</h3>
            <StatusPill configured={integ.configured} />
          </div>
          <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{integ.description}</p>
        </div>
        <div className="text-xs text-primary font-bold uppercase tracking-wider">{open ? "Hide" : "Configure"}</div>
      </button>

      {open && (
        <div className="px-5 pb-5 pt-2 border-t border-slate-100 bg-slate-50/60">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            {integ.fields.map((f) => (
              <FieldInput
                key={f.name}
                field={f}
                value={value[f.name]}
                onChange={(v) => setValue((cur) => ({ ...cur, [f.name]: v }))}
              />
            ))}
          </div>
          <div className="mt-5 flex items-center justify-between flex-wrap gap-3">
            <p className="text-[11px] text-slate-400">
              {integ.updated_at ? `Last updated ${new Date(integ.updated_at).toLocaleString("en-AE", { dateStyle: "medium", timeStyle: "short" })}` : "Never updated"}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={resetAll}
                data-testid={`integ-clear-${integ.key}`}
                className="h-10 px-4 rounded-lg border border-slate-200 hover:border-rose-300 hover:text-rose-700 text-slate-600 text-sm font-semibold inline-flex items-center gap-1.5 transition"
              >
                <Trash2 size={14} /> Clear
              </button>
              <button
                onClick={() => setValue(integ.value || {})}
                className="h-10 px-4 rounded-lg border border-slate-200 hover:border-slate-300 text-slate-600 text-sm font-semibold inline-flex items-center gap-1.5 transition"
              >
                <RotateCcw size={14} /> Revert
              </button>
              <button
                onClick={save}
                disabled={saving}
                data-testid={`integ-save-${integ.key}`}
                className="h-10 px-5 rounded-lg bg-primary hover:bg-primary-600 text-white text-sm font-bold inline-flex items-center gap-1.5 transition disabled:opacity-60"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminIntegrations() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/integrations");
      setItems(data);
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function onSaved(key, value) {
    setItems((cur) => cur.map((it) => it.key === key ? { ...it, value, configured: !!Object.keys(value || {}).length, updated_at: new Date().toISOString() } : it));
  }

  return (
    <div data-testid="admin-integrations" className="space-y-6 max-w-5xl">
      <div>
        <p className="text-[11px] uppercase tracking-[0.2em] text-primary-700 font-bold">System</p>
        <h1 className="font-display text-3xl font-bold text-slate-900 mt-1">Integrations</h1>
        <p className="text-slate-500 mt-1">Connect TESLAM to ENEC's identity, messaging and mapping providers. Secrets are encrypted at rest and masked on screen.</p>
      </div>

      {loading ? (
        <div className="h-40 grid place-items-center text-slate-400">
          <Loader2 size={20} className="animate-spin" />
        </div>
      ) : (
        <div className="grid gap-3" data-testid="integ-list">
          {items.map((it) => (
            <IntegrationCard key={it.key} integ={it} onSaved={onSaved} />
          ))}
        </div>
      )}

      <div className="mt-6 p-4 rounded-2xl bg-amber-50 border border-amber-200 text-sm text-amber-900">
        <p className="font-semibold">Heads up</p>
        <p className="mt-1 text-amber-800">
          Saving credentials here updates live runtime configuration immediately. Push notifications start flowing to registered devices as soon as Firebase is enabled — no redeploy required.
        </p>
      </div>
    </div>
  );
}
